import { LotI, UserCurrency } from '@tacans/shared-marketplace-layer';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import React, { useCallback, useMemo } from 'react';
import { generatePath, RouteE } from 'src/constants/pages.constants';
import { ShareModal } from 'src/features/common/app/modals/share-modal';
import { calculateMinMaxNftPrices } from 'src/features/common/collectible/collectible.helpers';
import { NftBlockchainIcon } from 'src/features/common/collectible/components/nft-blockchain-icon';
import NftCardAuctionTime from 'src/features/common/collectible/components/nft-card-auction-time';
import { ApproveBidAction } from 'src/features/common/collectible/containers/nft-actions/approve-bid-action';
import { BurnNftAction } from 'src/features/common/collectible/containers/nft-actions/burn-nft-action';
import { BuyNowAction } from 'src/features/common/collectible/containers/nft-actions/buy-now-action';
import { ChangePriceAction } from 'src/features/common/collectible/containers/nft-actions/change-price-action';
import { CreateLotAction } from 'src/features/common/collectible/containers/nft-actions/create-lot-acton';
import { DeleteNftAction } from 'src/features/common/collectible/containers/nft-actions/delete-nft-action';
import { NftContactSellerAction } from 'src/features/common/collectible/containers/nft-actions/nft-contact-seller-action';
import { NftMintAction } from 'src/features/common/collectible/containers/nft-actions/nft-mint-action';
import { NftPauseAction } from 'src/features/common/collectible/containers/nft-actions/nft-pause-action';
import { PlaceBidAction } from 'src/features/common/collectible/containers/nft-actions/nft-place-bid-action';
import { TransferNftAction } from 'src/features/common/collectible/containers/nft-actions/transfer-nft-action';
import { ViewBidsAction } from 'src/features/common/collectible/containers/nft-actions/view-bids.action';
import { useActiveLots, useAllLots, useCollectible } from 'src/features/common/collectible/hooks/collectible.hooks';
import { useIsActiveNftOwner } from 'src/features/common/collectible/hooks/use-is-active-nft-owner';
import { useIsCanEditCollectible } from 'src/features/common/collectible/hooks/use-is-can-edit-collectible';
import { useLotAvailableActions } from 'src/features/common/collectible/hooks/use-lot-available-actions';
import { useNftAvailableActions } from 'src/features/common/collectible/hooks/use-nft-available-actions';
import { INftMiscLocale, INftRoyaltiesLocale } from 'src/features/common/collectible/types/locales';
import Currency from 'src/features/common/currency/components/currency/currency';
import { FAQ_ITEMS } from 'src/features/common/faq/containers/constants';
import { useFiatCurrency } from 'src/features/common/profile/hooks/profile.hooks';
import { useDisplayName } from 'src/features/common/user/hooks/use-display-name.hooks';
import { useUserListEntities } from 'src/features/common/user/hooks/user-list.hooks';
import { useScreenDetect } from 'src/hooks/useScreenDetect';
import { Button } from 'src/ui/common/atoms/button';
import { FlexBox } from 'src/ui/common/atoms/flex-box';
import { Spacing } from 'src/ui/common/atoms/spacing';
import { SvgIcon } from 'src/ui/common/atoms/svg-icon';
import { Typography } from 'src/ui/common/atoms/typography';
import TooltipComponent from 'src/ui/common/organisms/tooltip/tooltip.component';
import { IconBox } from 'src/ui/white-labels/ocean/atoms/icon-box';
import { CardBox } from 'src/ui/white-labels/ocean/layouts/card-box';
import { downloadPrivateFile } from 'src/utils/downloadPrivateFile';
import { decodeHTMLEntities } from 'src/utils/html.utils';
import { getLotType, getNftTypeByStandard } from 'src/utils/lot.utils';
import {
    StyledActions,
    StyledFiatPrice,
    StyledLabel,
    StyledPriceRow,
    StyledRoyalties,
    StyledTitle,
    StyledLearnMore,
    StyledDescription,
} from './nft-main-info-container.styles';

export const NftMainInfoContainer = ({ onGoToLots }: { onGoToLots: () => void }) => {
    const router = useRouter();
    const nft = useCollectible();
    const { t } = useTranslation();
    const users = useUserListEntities();
    const allLots = useAllLots();
    const activeLots = useActiveLots();
    const isActiveNftOwner = useIsActiveNftOwner();
    const { isMultiple } = getNftTypeByStandard(nft?.standard!);
    const fiatCurrency = useFiatCurrency();
    const screen = useScreenDetect();

    const creatorName = useDisplayName(users[nft?.author!]);
    const canEdit = useIsCanEditCollectible();

    const locale = useMemo(
        () => ({
            by: t('labels.by', { ns: 'common' }),
            edit: t('crud.edit', { ns: 'actions' }),
            tooltip: t('nft.view.tooltip', { ns: 'pages' }),
            learnMore: t('common.learnMore', { ns: 'actions' }),
            misc: t('nft.view.misc', { returnObjects: true, ns: 'pages' }) as INftMiscLocale,
            royalties: t('nft.view.royalties', { returnObjects: true, ns: 'pages' }) as INftRoyaltiesLocale,
        }),
        [t]
    );

    const lotToDisplay = useMemo(() => {
        if (!isMultiple) {
            return isActiveNftOwner ? allLots?.find((lot: LotI) => !lot.isDeleted) : activeLots[0];
        }
        return undefined;
    }, [activeLots, allLots, isActiveNftOwner, isMultiple]);

    const actions = useNftAvailableActions({ nft: nft!, lotToDisplay });
    const lotActions = useLotAvailableActions({ nft: nft!, lot: lotToDisplay! });

    const openShareModal = useCallback(() => {
        ShareModal.open({
            previewImg: nft?.previewAssets?.webps?.smallUrl ?? nft?.previewAssets?.originalUrl!,
            title: nft?.name!,
            subTitle: `${locale.by} ${creatorName}`,
            url: generatePath(RouteE.COLLECTIBLE_DETAILS, { id: nft?.id! }),
        });
    }, [locale, creatorName, nft]);

    const nftPrice = useMemo(() => calculateMinMaxNftPrices(activeLots), [activeLots]);

    const { isAuction } = getLotType(lotToDisplay?.type!);

    const isMultipleWithTokens = isMultiple && !actions.delete;

    const buttonSize = screen.xs ? 'md' : 'lg';

    const actionsCount = useMemo(() => {
        // Skip actions out of grid scope
        const { download, share, ...filteredActions } = actions;
        return [
            ...Object.values(filteredActions),
            ...Object.values(lotActions),
            isAuction,
            isMultipleWithTokens,
        ].filter((v) => v).length;
    }, [actions, lotActions, isAuction, isMultipleWithTokens]);

    const onEditClick = useCallback(() => {
        nft?.id &&
            router.push(
                generatePath(RouteE.COLLECTIBLE_EDIT, {
                    id: nft.id,
                })
            );
    }, [nft, router]);

    const handleDownloadNft = useCallback(() => {
        downloadPrivateFile('/v2/nft/download', nft?.id!);
    }, [nft?.id]);

    if (!nft) {
        return null;
    }

    return (
        <CardBox>
            <CardBox.Content>
                <StyledTitle justifyContent="space-between" alignItems="flex-start">
                    <Typography size="base" sizeMd="md" tag="h1" color="warmBlack">
                        {decodeHTMLEntities(nft.name)}
                    </Typography>
                    <FlexBox>
                        {canEdit && (
                            <Spacing mr="5px">
                                <Button onClick={onEditClick} variant="o_purple" size={buttonSize}>
                                    {locale.edit}
                                </Button>
                            </Spacing>
                        )}
                        {actions.download && (
                            <Spacing mr="5px">
                                <IconBox icon="download" size={34} iconSize={18} onClick={handleDownloadNft} />
                            </Spacing>
                        )}
                        {actions.share && <IconBox icon="share" size={34} iconSize={18} onClick={openShareModal} />}
                    </FlexBox>
                </StyledTitle>
                <StyledPriceRow alignItems="flex-end" justifyContent="space-between">
                    <div>
                        <StyledFiatPrice>
                            <StyledLabel size="xs" tag="div">
                                {isMultiple ? locale.misc.minPrice : locale.misc.price}
                            </StyledLabel>
                            <FlexBox alignItems="center">
                                {nftPrice.minNanoPrice ? (
                                    <>
                                        <NftBlockchainIcon blockchainId={nft.blockchainId} />
                                        <Currency nanoPrice={nftPrice.minNanoPrice} blockchainId={nft.blockchainId} />
                                    </>
                                ) : (
                                    'Not on Sale'
                                )}
                            </FlexBox>
                        </StyledFiatPrice>
                        {!!nftPrice.minNanoPrice && (
                            <div>
                                <StyledLabel size="xs" tag="div">
                                    {fiatCurrency === UserCurrency.USD
                                        ? locale.misc.priceInDollars
                                        : locale.misc.priceInEuro}
                                </StyledLabel>
                                <Currency
                                    toEURO={fiatCurrency === UserCurrency.EUR}
                                    toUSD={fiatCurrency === UserCurrency.USD}
                                    blockchainId={nft.blockchainId}
                                    nanoPrice={nftPrice.minNanoPrice}
                                />
                            </div>
                        )}
                    </div>
                    <StyledRoyalties>
                        <FlexBox alignItems="center">
                            <StyledLabel size="xs" tag="div">
                                {locale.royalties.label}
                            </StyledLabel>
                            <TooltipComponent
                                content={
                                    <div>
                                        <StyledDescription size="xs" color="warmBlack">
                                            {locale.tooltip}
                                        </StyledDescription>
                                        <a
                                            href={`${RouteE.FAQ}#${FAQ_ITEMS.ROYALTIES}`}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            <StyledLearnMore size="xs" color="warmBlack">
                                                {locale.learnMore}
                                            </StyledLearnMore>
                                        </a>
                                    </div>
                                }
                                position="bottom-end"
                                hasTooltipInteraction
                            >
                                <Spacing mb="12px" ml="8px">
                                    <SvgIcon variant="bold" icon="information" width={17} height={17} />
                                </Spacing>
                            </TooltipComponent>
                        </FlexBox>
                        {nft.royalties ?? '0'}%
                    </StyledRoyalties>
                </StyledPriceRow>
                <StyledActions actionsCount={actionsCount}>
                    {isAuction && (
                        <Button variant="o_white" size={buttonSize} stretch>
                            <SvgIcon variant="bold" icon="auction" />
                            <NftCardAuctionTime
                                type="Button"
                                start={lotToDisplay?.auctionStartAt!}
                                end={lotToDisplay?.auctionEndAt!}
                            />
                        </Button>
                    )}
                    {actions.createLot && <CreateLotAction nft={nft} />}
                    {actions.mint && <NftMintAction nft={nft} />}
                    {isMultipleWithTokens && (
                        <Button
                            size={buttonSize}
                            onClick={onGoToLots}
                            variant={actions.mint || actions.createLot ? 'o_purple' : 'o_white'}
                        >
                            {activeLots.length > 0 ? locale.misc.buyItNow : locale.misc.goToLots}
                        </Button>
                    )}
                    {lotActions.sell && lotToDisplay && <ChangePriceAction nft={nft} lot={lotToDisplay} type="SELL" />}
                    {lotActions.placeBid && lotToDisplay && <PlaceBidAction nft={nft} lot={lotToDisplay} />}
                    {lotActions.changePrice && lotToDisplay && (
                        <ChangePriceAction nft={nft} lot={lotToDisplay} type="CHANGE_PRICE" />
                    )}
                    {lotActions.approve && lotToDisplay && <ApproveBidAction nft={nft} lot={lotToDisplay} />}
                    {lotActions.viewBids && lotToDisplay && (
                        <ViewBidsAction
                            nft={nft}
                            lot={lotToDisplay}
                            variant={
                                lotActions.placeBid || lotActions.approve || lotActions.changePrice
                                    ? 'o_white'
                                    : 'o_blue'
                            }
                        />
                    )}
                    {lotActions.buyNow && lotToDisplay && <BuyNowAction nft={nft} lot={lotToDisplay} />}
                    {lotActions.pause && lotToDisplay && <NftPauseAction lot={lotToDisplay} nft={nft} />}
                    {actions.transfer && <TransferNftAction nft={nft} />}
                    {actions.burn && <BurnNftAction nft={nft} />}
                    {actions.contactSeller && <NftContactSellerAction />}
                    {actions.delete && <DeleteNftAction nft={nft} />}
                </StyledActions>
            </CardBox.Content>
        </CardBox>
    );
};
